/* eslint-disable no-console */
import fetch from 'unfetch'
import isEqual from 'lodash.isequal'
import { Raw } from 'slate'
import cloneDeep from 'lodash.clonedeep'

export function getTransformedImageSource({
  mediaId,
  transforms: { crop, rotation, mirror } = {},
}) {
  let src = `/images/${mediaId}?`
  if (crop) {
    src += `crop=${crop.x},${crop.y},${crop.width},${crop.height}&`
  }
  if (rotation) {
    src += `rotation=${rotation}&`
  }
  if (mirror) {
    src += 'mirror=1&'
  }
  return src
}

export function getSlide(
  unit,
  sectionIndex,
  slideIndex,
  slideType = 'presentation',
) {
  const section = getSection(unit, sectionIndex)
  return section[slideType].slides[slideIndex]
}

export function getSlides(unit, slideType = 'presentation') {
  return unit.sections.reduce((slides, section, sectionIndex) => {
    return slides.concat(
      ...section.subsections.map((section, subsectionIndex) =>
        getSlidesFromSection(
          section,
          [sectionIndex, subsectionIndex],
          slideType,
        ),
      ),
    )
  }, [])
}

export const hasAssessments = ({ summativePracticalAssessments }) => {
  return (
    summativePracticalAssessments &&
    (summativePracticalAssessments.length > 0 &&
      summativePracticalAssessments.every(
        assessment =>
          assessment.learnerGuideContent &&
          !emptyContentSection(assessment.learnerGuideContent) &&
          assessment.assessmentPlan &&
          Object.keys(assessment.assessmentPlan).length > 1,
      ))
  )
}

export function getSlidesFromSection(section, sectionIndex, slideType) {
  if (
    !section.presentation ||
    !section[slideType] ||
    !section[slideType].slides
  ) {
    return []
  }
  return section[slideType].slides
    .map((slide, slideIndex) => ({ ...slide, slideIndex, sectionIndex }))
    .concat(
      ...(!section.subsections
        ? []
        : section.subsections.map((section, subsectionIndex) =>
          getSlidesFromSection(
            section,
            [...sectionIndex, subsectionIndex],
            slideType,
          ),
        )),
    )
}

export function getSectionIndexArray(sectionIndex) {
  if (typeof sectionIndex === 'string') {
    sectionIndex = sectionIndex.split('.')
  }
  return sectionIndex.map(
    num => (typeof num === 'string' ? parseInt(num, 10) : num),
  )
}

export function sectionIndexToPath(sectionIndex) {
  return sectionIndex.split('.').join('.subsections.')
}

export function getSection(unit, sectionIndex) {
  let section
  for (const index of getSectionIndexArray(sectionIndex)) {
    section = section ? section.subsections[index] : unit.sections[index]
  }
  return section
}

export function getQuestion(unit, sectionIndex, questionIndex) {
  const section = getSection(unit, sectionIndex)
  return section.questions[questionIndex] || false
}

export function hasElement(element, elements) {
  return elements.some(
    ({ type, index }) => element.type === type && isEqual(element.index, index),
  )
}

export function sectionDisplayIndex(...sectionIndex) {
  return (Array.isArray(sectionIndex[0]) ? sectionIndex[0] : sectionIndex)
    .map(i => ++i)
    .join('.')
}

export function getSectionHeading(unit, sectionIndex) {
  const section = getSection(unit, sectionIndex)
  return `${sectionDisplayIndex(...sectionIndex)} ${section.name}`
}

export function theoryQuestionsBySection(sections, questionType) {
  const questionsArr = []
  const processSection = ({ subsections = [], questions = [] }) => {
    questionsArr.push(...questions.filter(({ type }) => type === questionType))
    subsections.forEach(processSection)
  }
  sections.forEach(processSection)
  return questionsArr
}

export function allTheoryQuestions(sections, questionType) {
  const questions = []
  sections.map(({ subsections }) =>
    questions.push(...theoryQuestionsBySection(subsections, questionType)),
  )
  return questions
}

export function serializeContentSection(state) {
  const { document } = Raw.serialize(state)
  return { nodes: cleanNodes(document.nodes) }
}
export function deserializeContentSection(state) {
  if (state == null || !state.nodes || state.nodes.length === 0) {
    state = {
      nodes: [
        {
          kind: 'block',
          type: 'paragraph',
          nodes: [
            {
              kind: 'text',
              text: '',
            },
          ],
        },
      ],
    }
  }
  return Raw.deserialize(cloneDeep(state), { terse: true })
}

export function cleanNodes(nodes, fn) {
  return (
    nodes &&
    nodes
      .filter(({ type }) => type !== 'null')
      .map(({ //eslint-disable-next-line no-unused-vars
        isVoid, nodes, ...node }) => {
        if (typeof fn === 'function') {
          nodes = nodes.map(fn)
        }
        return {
          ...node,
          nodes: cleanNodes(nodes, fn),
        }
      })
  )
}

export function flattenTextList(textList, depth = 0) {
  return [].concat(
    ...textList.map(({ text, items = [] }) => [
      '#'.repeat(depth) + text,
      ...flattenTextList(items, depth + 1),
    ]),
  )
}

export function nestTextList(flatTextList) {
  const textlist = []
  for (const rawtext of flatTextList) {
    let [, hashes, text] = /(#*)\s*(.*)/.exec(rawtext)
    let baseItemslist = textlist,
      { length } = hashes
    while (0 < length--) {
      try {
        baseItemslist = baseItemslist[baseItemslist.length - 1].items
      } catch (error) {
        alert(
          `Cannot have orphaned items. Please ensure your list increases by only 1 depth level at "${text}"`,
        )
        throw new Error('cannot have orphaned items')
      }
      if (!baseItemslist) {
        alert(
          `Cannot have orphaned items. Please ensure your list increases by only 1 depth level at "${text}"`,
        )
        throw new Error('cannot have orphaned items')
      }
    }
    baseItemslist.push({
      text,
      items: [],
    })
  }
  return textlist
}

export function flattenNestedObject(obj, listKey) {
  const { [listKey]: list = [], ...restObj } = obj
  return [].concat(
    restObj,
    ...list.map(item => flattenNestedObject(item, listKey)),
  )
}

export const hasLearnerGuideContent = unit => {
  const emptySections = []
  unit.sections.forEach((section, i) => {
    section.subsections.forEach(({ content, subsections }, j) => {
      if (emptyContentSection(content))
        emptySections.push(getSectionHeading(unit, [i, j]))
      subsections.forEach(({ content, subsections }, k) => {
        if (emptyContentSection(content))
          emptySections.push(getSectionHeading(unit, [i, j, k]))
        subsections.forEach(({ content }, l) => {
          if (emptyContentSection(content))
            emptySections.push(getSectionHeading(unit, [i, j, k, l]))
        })
      })
    })
  })
  return emptySections
}

export function emptyContentSection(contentSection) {
  // a content section is empty if
  return (
    // if the value in null or undefined
    contentSection == null ||
    // the nodes key is null or undefined
    contentSection.nodes == null ||
    // the nodes array is empty
    contentSection.nodes.length === 0 ||
    // the nodes all resolve to an empty string
    contentSectionToText(contentSection) === ''
  )
}

export function contentSectionToText({ nodes = [], ranges = [] } = {}) {
  return (
    nodes.map(contentSectionToText).join('') +
    ranges.map(({ text }) => text).join('')
  )
}

const imageRequests = Object.create(null)
export async function getAuthenticatedImage(source, token) {
  if (!imageRequests[source]) {
    imageRequests[source] = new Promise(async (resolve, reject) => {
      try {
        const response = await fetch(source, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
        if (!response.ok) {
          resolve('/not-found.png')
        } else {
          const blob = await response.blob()
          resolve(URL.createObjectURL(blob))
        }
      } catch (err) {
        reject(err)
      }
    })
  }
  return await imageRequests[source]
}


/*
 * Update July 2020
 * Tasks becoming a bit more structured - based on hashes and underscores
 * # Length;
 *  0 = this is a grey header item with a, b, c numbering on the list
 *  1 = this is a grey header item with i, ii, iii numbering on the list and an intent
 *  2 = this is a question, no list numbering on these items and will have Y / N columns at the end
 * _ Length;
 *  2 = this is a bullet point with blue diamond.  These bullet points can be under any of the above items
 */

export function serializeTasks(tasksToSerialize) {
  console.log('Tasks to Serialize', tasksToSerialize);
  const tasks = []
  for (const { task, mapping } of tasksToSerialize) {
    const [, hashes, underscores, text] = /(#*)(_*)\s*(.*)/.exec(task)

    if ((hashes.length > 0 || underscores.length > 0) && tasks.length === 0) {
      alert('cannot have empty items - ie, missing text after __ or #.')
      throw new Error('cannot have empty items')
    }
    if (underscores.length === 2) {
      if (tasks.length == 0) {
        alert('cannot have bullets as the first item.')
        throw new Error('bullets must be below tasks or subtasks')
      }
      let addToItem = tasks[tasks.length - 1]
      if (addToItem.subtasks.length > 0) {
        // if any subtasks exist, then the bullets must be below that
        addToItem = addToItem.subtasks[addToItem.subtasks.length - 1]
      }
      if (addToItem.questions.length > 0) {
        console.log('adding bullet to a question');
        // if the task / subtask has any questions, bullets must be below the question
        addToItem = addToItem.questions[addToItem.questions.length - 1]
      }
      addToItem.bullets.push({
        task: text
      })
    } else {
      switch (hashes.length) {
        default:
        case 0: // no hashes is a heading
          tasks.push({ task: text, subtasks: [], questions: [], bullets: [], mapping, indent: 0 })
          break
        case 1: // one # is a sub-heading
          tasks[tasks.length - 1].subtasks.push({
            task: text,
            // subtasks: [],  system does not build sub-subtasks anymore
            questions: [],
            bullets: [],
            mapping,
            indent: 1
          })
          break
        case 2: // two hashes is a question
          let addToItem = tasks[tasks.length - 1]
          if (addToItem.subtasks.length > 0) {
            addToItem = addToItem.subtasks[addToItem.subtasks.length - 1]
          }
          addToItem.questions.push({
            task: text,
            mapping,
            bullets: []
          })
          break
      }
    }
  }
  console.log('Serialized tasks', tasks);
  return tasks
}

export function deserializeTasks(tasksToDeserialize) {
  console.log('Serialized tasks', tasksToDeserialize);
  const tasks = []
  for (const { subtasks, questions, bullets, ...task } of cloneDeep(tasksToDeserialize)) {
    tasks.push(task)
    tasks.push(...pushBullets(bullets));
    tasks.push(...pushQuestions(questions));
    for (const { questions, bullets, ...task } of subtasks) {
      task.task = `# ${task.task}`
      tasks.push(task)
      tasks.push(...pushBullets(bullets));
      tasks.push(...pushQuestions(questions));
      // for (const task of subsubtasks) {
      //   task.task = `## ${task.task}`
      //   tasks.push(task)
      // }
    }
  }
  return tasks
}

function pushQuestions(questions) {
  const tasks = []
  if (questions && questions.length > 0) {
    for (const { bullets, ...task } of questions) {
      task.task = `## ${task.task}`
      tasks.push(task)
      tasks.push(...pushBullets(bullets));
    }
  }
  return tasks
}

function pushBullets(bullets) {
  const tasks = []
  if (bullets && bullets.length > 0) {
    for (const { ...task } of bullets) {
      task.task = `__ ${task.task}`
      tasks.push(task)
    }
  }
  return tasks
}





export function range(min, max, step = 1) {
  return Array.from(
    (function* () {
      while (min < max) {
        yield min
        min += step
      }
    })(),
  )
}

export const alphabet = range(97, 123).map(code => String.fromCharCode(code))

const sortNumericArray = (a, b) => {
  if (isEqual(a, b)) return 0
  if (a[0] == null) return 1
  if (b[0] == null) return -1
  if (a[0] > b[0]) return 1
  if (a[0] < b[0]) return -1
  return sortNumericArray(a.slice(1), b.slice(1))
}

const ELEMENT_TYPES = [
  'performanceCriteria',
  'performanceEvidence',
  'knowledgeEvidence',
]

export const sortMapping = mapping =>
  [...mapping].sort(
    (a, b) =>
      ELEMENT_TYPES.indexOf(a.type) > ELEMENT_TYPES.indexOf(b.type)
        ? 1
        : ELEMENT_TYPES.indexOf(a.type) < ELEMENT_TYPES.indexOf(b.type)
          ? -1
          : sortNumericArray(a.index, b.index),
  )



// WEBPACK FOOTER //
// ./src/lib/index.js